import './style/waitlist.scss';
import {AwesomeIcon} from '@dvrd/dvr-controls';
import classNames from 'classnames';

import React, {MouseEventHandler, useMemo} from 'react';
import {WaitlistDay, WaitlistDisplay} from '../../enums';
import WaitlistEntry from '../../models/waitlistEntryModel';

type MoveFunction = (entry: WaitlistEntry, direction: 'up' | 'down' | 'left' | 'right') => MouseEventHandler;

interface Props {
    onClickEntry: (entry: WaitlistEntry) => MouseEventHandler;
    onAddEntry: MouseEventHandler;
    onMoveEntry: MoveFunction;
    onClickDay: (day: WaitlistDay) => MouseEventHandler;
    entries: Record<WaitlistDay, Array<WaitlistEntry>>;
    loading: boolean;
}

interface DayProps extends Props {
    onToggleDisplay: MouseEventHandler;
    onChangeDay: (direction: 'left' | 'right') => MouseEventHandler;
    display: WaitlistDisplay;
    day: WaitlistDay;
}

function renderMoveControls(entry: WaitlistEntry, entries: Array<WaitlistEntry>, onMove: MoveFunction, noMob: boolean = true) {
    let upHidden: boolean, downHidden: boolean, leftHidden: boolean, rightHidden: boolean;
    if (entry.days.length !== 1) {
        upHidden = true;
        downHidden = true;
        leftHidden = true;
        rightHidden = true;
    } else {
        const day = entry.days[0];
        upHidden = entry.index === Math.min(...entries.map((entry: WaitlistEntry) => entry.index));
        downHidden = entry.index === Math.max(...entries.map((entry: WaitlistEntry) => entry.index));
        leftHidden = day === WaitlistDay.MONDAY;
        rightHidden = day === WaitlistDay.FRIDAY;
    }
    return (
        <div className={classNames('move-controls', noMob && 'no-mob')}>
            <AwesomeIcon name='chevron-up' className={classNames('move-control up', upHidden && 'hidden')}
                         onClick={onMove(entry, 'up')}/>
            <AwesomeIcon name='chevron-right' className={classNames('move-control right', rightHidden && 'hidden')}
                         onClick={onMove(entry, 'right')}/>
            <AwesomeIcon name='chevron-down' className={classNames('move-control down', downHidden && 'hidden')}
                         onClick={onMove(entry, 'down')}/>
            <AwesomeIcon name='chevron-left' className={classNames('move-control left', leftHidden && 'hidden')}
                         onClick={onMove(entry, 'left')}/>
        </div>
    );
}

function WeekWaitlist(props: Props) {
    const {onClickEntry, entries, onMoveEntry, onClickDay} = props;

    function renderDay(day: WaitlistDay) {
        const _entries = entries[day];
        return (
            <div className='day'>
                {_entries.map(renderEntry(day))}
            </div>
        );
    }

    function renderEntry(day: WaitlistDay) {
        return function (entry: WaitlistEntry) {
            return (
                <div key={entry.id} className='entry' onClick={onClickEntry(entry)}>
                    {renderMoveControls(entry, entries[day], onMoveEntry)}
                    <label className='customer-name'>{entry.customerName}</label>
                    <label className='dog-name'>{entry.dogName}</label>
                    {renderEntryInfo(entry, day)}
                </div>
            );
        };
    }

    function renderEntryInfo(entry: WaitlistEntry, day: WaitlistDay) {
        if (!entry.hasExtraData()) return null;
        const infoLeft = [WaitlistDay.THURSDAY, WaitlistDay.FRIDAY].includes(day);
        return (
            <div className='entry-info-wrapper no-mob'>
                <AwesomeIcon name='info-circle' className='info-icon'/>
                <div className={classNames('entry-info-container', infoLeft && 'left')}>
                    {!!entry.tel && <a href={`tel:${entry.tel}`} className='default-link'>{entry.tel}</a>}
                    {!!entry.email && <a href={`mailto:${entry.email}`} className='default-link'>{entry.email}</a>}
                    {!!entry.comment && <p className='comment'>{entry.comment}</p>}
                </div>
            </div>
        );
    }

    return (
        <div className='waitlist'>
            <div className='day-labels'>
                <div className='day-label-wrapper' onClick={onClickDay(WaitlistDay.MONDAY)}>
                    <label className='day-label no-mob'>Maandag</label>
                    <label className='day-label only-mob'>Ma</label>
                </div>
                <div className='day-label-wrapper' onClick={onClickDay(WaitlistDay.TUESDAY)}>
                    <label className='day-label no-mob'>Dinsdag</label>
                    <label className='day-label only-mob'>Di</label>
                </div>
                <div className='day-label-wrapper' onClick={onClickDay(WaitlistDay.WEDNESDAY)}>
                    <label className='day-label no-mob'>Woensdag</label>
                    <label className='day-label only-mob'>Wo</label>
                </div>
                <div className='day-label-wrapper' onClick={onClickDay(WaitlistDay.THURSDAY)}>
                    <label className='day-label no-mob'>Donderdag</label>
                    <label className='day-label only-mob'>Do</label>
                </div>
                <div className='day-label-wrapper' onClick={onClickDay(WaitlistDay.FRIDAY)}>
                    <label className='day-label no-mob'>Vrijdag</label>
                    <label className='day-label only-mob'>Vr</label>
                </div>
                <div className='day-label-wrapper gray' onClick={onClickDay(WaitlistDay.MISC)}>
                    <label className='day-label no-mob'>Overig</label>
                    <label className='day-label only-mob'>?</label>
                </div>
            </div>
            {renderDay(WaitlistDay.MONDAY)}
            {renderDay(WaitlistDay.TUESDAY)}
            {renderDay(WaitlistDay.WEDNESDAY)}
            {renderDay(WaitlistDay.THURSDAY)}
            {renderDay(WaitlistDay.FRIDAY)}
            {renderDay(WaitlistDay.MISC)}
            <div className='pad'/>
        </div>
    );
}

const dutchWeekdays = ['Maandag', 'Dinsdag', 'Woensdag', 'Donderdag', 'Vrijdag', 'Overig'];

function DayWaitlist(props: DayProps) {
    const {onClickEntry, entries, onMoveEntry, day, onChangeDay} = props;
    const _entries = useMemo(() => {
        return entries[day];
    }, [entries, day]);

    function renderHeader() {
        return (
            <div className={classNames('day-label-container', day === WaitlistDay.MISC && 'gray')}>
                <AwesomeIcon name='chevron-left'
                             className={classNames('day-control', day === WaitlistDay.MONDAY && 'hidden')}
                             onClick={onChangeDay('left')}/>
                <label className='day-label'>{dutchWeekdays[Number(day)]}</label>
                <AwesomeIcon name='chevron-right'
                             className={classNames('day-control', day === WaitlistDay.MISC && 'hidden')}
                             onClick={onChangeDay('right')}/>
            </div>
        );
    }

    function renderEntry(entry: WaitlistEntry) {
        const {customerName, dogName, tel, email, comment} = entry;
        return (
            <div className='waitlist-entry' key={entry.id} onClick={onClickEntry(entry)}>
                {renderMoveControls(entry, _entries, onMoveEntry, false)}
                {!!customerName && <label className='main-label'>Klant: {customerName}</label>}
                {!!dogName && <label className='main-label'>Hond: {dogName}</label>}
                {!!tel && <a href={`tel:${tel}`} className='default-link' target='_blank'>{tel}</a>}
                {!!email && <a href={`mailto:${email}`} className='default-link' target='_blank'>{email}</a>}
                {!!comment && <p className='comment'>{comment}</p>}
            </div>
        );
    }

    return (
        <div className='day-waitlist'>
            {renderHeader()}
            <div className='entries-container'>
                {_entries.map(renderEntry)}
            </div>
        </div>
    );
}

export default function Waitlist(props: DayProps) {
    const {onAddEntry, display, onToggleDisplay} = props;

    return (
        <div className='default-page'>
            {display === WaitlistDisplay.WEEK && <WeekWaitlist {...props}/>}
            {display === WaitlistDisplay.DAY && <DayWaitlist {...props}/>}
            <div className='action-controls'>
                <div className='btn-display click' onClick={onToggleDisplay}>
                    <AwesomeIcon name={display === WaitlistDisplay.WEEK ? 'bars' : 'table'} className='action-icon'/>
                </div>
                <div className='btn-add click' onClick={onAddEntry}>
                    <AwesomeIcon name='plus' className='action-icon'/>
                </div>
            </div>
        </div>
    );
}

Waitlist.displayName = 'Waitlist';